import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

// --- CONSTANTS ---
const TITLES = ['Mr.', 'Miss', 'Mrs.', 'Ms.', 'Dr.', 'Prof.', 'Rev.', 'Eng.', 'Hon.'];
const COUNTRIES = [
  'Ghana', 'Nigeria', 'United Kingdom', 'United States', 'Canada', 'Germany', 'France', 'South Africa', 'Kenya', 'Togo', 'Ivory Coast', 'Other'
];

const OTP_CSRF = 'hJDRTxO0QPa8awJt59YYXdVlKlk0DwXtF57wT35gBaCq9YfXgQ6lPe4NNUkHHN13';

// --- COMPONENTS ---

const InputField = ({ label, value, onChange, type = "text", required = false, readOnly = false, options = null }) => (
  <div className="space-y-1.5">
    <label className="text-[10px] uppercase tracking-widest text-gray-500 font-medium">
      {label} {required && <span className="text-red-400">*</span>}
    </label>
    {options ? (
      <div className="relative">
        <select
          value={value}
          onChange={onChange}
          disabled={readOnly}
          className="w-full bg-transparent border-b border-gray-200 py-2.5 text-sm text-gray-900 focus:border-black focus:outline-none transition-colors appearance-none disabled:text-gray-400"
        >
          <option value="">Select...</option>
          {options.map(opt => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
        <span className="absolute right-0 top-3 pointer-events-none text-gray-400 text-[10px]">▼</span>
      </div>
    ) : (
      <input
        type={type}
        value={value}
        onChange={onChange}
        readOnly={readOnly}
        className="w-full bg-transparent border-b border-gray-200 py-2.5 text-sm text-gray-900 placeholder-gray-300 focus:border-black focus:outline-none transition-colors disabled:text-gray-400"
      />
    )}
  </div>
);

export default function RequestAttire() {
  const navigate = useNavigate();
  const successRef = useRef(null);

  // --- STATE ---
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    bookingDate: '',
    bookingTime: '',
  });

  // Slots state
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsAvailable, setSlotsAvailable] = useState([]);
  const [slotsBooked, setSlotsBooked] = useState([]);

  const today = new Date().toISOString().split('T')[0];

  // --- EFFECTS ---

  // 1. Load User Data
  useEffect(() => {
    const token = localStorage.getItem('emba_access');
    const userData = localStorage.getItem('emba_user');

    if (!token || !userData) {
      // Redirect to login if not authenticated? Or allow guest?
      // User request implies "populated with user info", so likely auth required.
      // For now, we'll redirect to login if missing.
      navigate('/login');
      return;
    }

    try {
      const parsed = JSON.parse(userData);
      const u = parsed.user || parsed; // Handle nested user object
      setUser(u);
    } catch (e) {
      console.error("Failed to parse user", e);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  // 2. Fetch Slots
  useEffect(() => {
    if (!form.bookingDate) {
      setSlotsAvailable([]);
      setSlotsBooked([]);
      return;
    }

    const fetchSlots = async () => {
      setSlotsLoading(true);
      try {
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/appointments/available-slots/?date=${form.bookingDate}`);
        const data = await res.json().catch(() => ({}));
        if (res.ok) {
          setSlotsAvailable(data.available || []);
          setSlotsBooked(data.booked || []);
        }
      } catch (e) {
        console.error("Failed to fetch slots", e);
      } finally {
        setSlotsLoading(false);
      }
    };

    fetchSlots();
  }, [form.bookingDate]);


  // --- HANDLERS ---

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    if (!form.bookingDate || !form.bookingTime) {
      setError("Please select a date and time.");
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('emba_access');
      const payload = {
        appointment_user_id: user.id,
        appointment_date: form.bookingDate,
        appointment_time: form.bookingTime.length === 5 ? `${form.bookingTime}:00` : form.bookingTime, // Ensure HH:MM:SS format if needed, or just HH:MM
        paid: true, // As per curl example
        amount: "0"
      };

      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/appointments/`, {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-CSRFTOKEN': OTP_CSRF
        },
        body: JSON.stringify(payload)
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data?.detail || data?.message || 'Failed to create appointment');
      }

      setSuccessMessage('Appointment booked successfully!');
      setForm({ bookingDate: '', bookingTime: '' });
      setTimeout(() => {
        navigate('/my-bookings');
      }, 2000);

    } catch (err) {
      setError(String(err?.message || err));
    } finally {
      setSubmitting(false);
    }
  };

  // --- RENDER HELPERS ---

  const slotStart = (range) => String(range).split(' - ')[0];

  if (loading) return <div className="min-h-screen flex items-center justify-center text-xs tracking-widest uppercase">Loading...</div>;

  return (
    <div className="min-h-screen bg-neutral-50 font-sans text-gray-900">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <Link to="/" className="text-xs tracking-[0.2em] font-bold uppercase hover:opacity-70 transition-opacity">
            EMBA by Emefa
          </Link>

          <div className="flex items-center gap-6">
            {user && (
              <span className="text-[10px] tracking-widest uppercase font-medium text-gray-500 hidden md:block">
                Welcome, {user.first_name}
              </span>
            )}
            <nav className="hidden md:flex gap-8 text-[10px] tracking-widest uppercase font-medium text-gray-500">
              <Link to="/" className="hover:text-black transition-colors">Home</Link>
              <Link to="/profile" className="hover:text-black transition-colors">Account</Link>
              {user && user.role === 'ADMIN' && (
                <Link to="/all-appointments" className="hover:text-black transition-colors">All Appointments</Link>
              )}
            </nav>
            <Link to="/my-bookings" className="text-[10px] uppercase tracking-widest bg-black text-white px-4 py-2 hover:bg-gray-800 transition-colors">
              My Bookings
            </Link>
          </div>
        </div>
      </header>

      <main className="pt-32 pb-20 px-4 md:px-6">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-12 text-center"
          >
            <h1 className="text-3xl md:text-4xl font-serif mb-3">Book an Appointment</h1>
            <p className="text-sm text-gray-500 tracking-wide">Schedule your consultation with our designers.</p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column: User Details (Read Only) */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="lg:col-span-1 bg-white p-8 shadow-sm border border-gray-100 h-fit"
            >
              <h2 className="text-sm font-serif font-medium mb-6 pb-4 border-b border-gray-50">Your Details</h2>
              <div className="space-y-6">
                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-1">
                    <InputField label="Title" value={user?.title || ''} readOnly />
                  </div>
                  <div className="col-span-2">
                    <InputField label="First Name" value={user?.first_name || ''} readOnly />
                  </div>
                </div>
                <InputField label="Last Name" value={user?.last_name || ''} readOnly />
                <InputField label="Phone" value={user?.phone_number || ''} readOnly />
                <InputField label="Email" value={user?.email || ''} readOnly />
                <div className="grid grid-cols-2 gap-4">
                  <InputField label="Country" value={user?.country || ''} readOnly />
                  <InputField label="City" value={user?.city || ''} readOnly />
                </div>
              </div>
              <p className="mt-6 text-[10px] text-gray-400 italic">
                To update these details, please visit your <Link to="/profile" className="underline">Profile</Link>.
              </p>
            </motion.div>

            {/* Right Column: Booking Form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="lg:col-span-2 bg-white p-8 shadow-sm border border-gray-100"
            >
              <h2 className="text-sm font-serif font-medium mb-6 pb-4 border-b border-gray-50">Select Date & Time</h2>

              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Date Selection */}
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-gray-500 font-medium mb-3">
                    Date
                  </label>
                  <input
                    type="date"
                    min={today}
                    value={form.bookingDate}
                    onChange={(e) => setForm({ ...form, bookingDate: e.target.value, bookingTime: '' })}
                    className="w-full md:w-1/2 bg-transparent border-b border-gray-200 py-2.5 text-sm text-gray-900 focus:border-black focus:outline-none"
                    required
                  />
                </div>

                {/* Time Selection */}
                <AnimatePresence>
                  {form.bookingDate && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                    >
                      <label className="block text-[10px] uppercase tracking-widest text-gray-500 font-medium mb-4">
                        Available Slots
                      </label>

                      {slotsLoading ? (
                        <div className="text-xs text-gray-400 italic">Loading slots...</div>
                      ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                          {slotsAvailable.length === 0 && slotsBooked.length === 0 ? (
                            <div className="col-span-full text-xs text-gray-400">No slots available for this date.</div>
                          ) : (
                            <>
                              {slotsAvailable.map(slot => {
                                const start = slotStart(slot);
                                // Check if slot is in the past for today
                                const now = new Date();
                                const currentHour = now.getHours();
                                const currentMinute = now.getMinutes();
                                const [slotHour, slotMinute] = start.split(':').map(Number);

                                const isToday = form.bookingDate === today;
                                const isPast = isToday && (slotHour < currentHour || (slotHour === currentHour && slotMinute < currentMinute));

                                if (isPast) {
                                  return (
                                    <button
                                      key={slot}
                                      type="button"
                                      disabled
                                      className="py-2 px-3 text-xs border border-gray-100 bg-gray-50 text-gray-400 rounded cursor-not-allowed flex items-center justify-center gap-1"
                                    >
                                      <span>{slot}</span>
                                      <span className="text-[8px] font-bold uppercase tracking-wider text-red-300">Past</span>
                                    </button>
                                  );
                                }

                                return (
                                  <button
                                    key={slot}
                                    type="button"
                                    onClick={() => setForm({ ...form, bookingTime: start })}
                                    className={`py-2 px-3 text-xs border rounded transition-all ${form.bookingTime === start
                                      ? 'bg-black text-white border-black'
                                      : 'bg-white text-gray-600 border-gray-200 hover:border-black'
                                      }`}
                                  >
                                    {slot}
                                  </button>
                                );
                              })}
                              {slotsBooked.map(slot => (
                                <button
                                  key={slot}
                                  type="button"
                                  disabled
                                  className="py-2 px-3 text-xs border border-gray-100 bg-gray-50 text-gray-300 rounded cursor-not-allowed line-through"
                                >
                                  {slot}
                                </button>
                              ))}
                            </>
                          )}
                        </div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Messages */}
                <AnimatePresence>
                  {error && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-3 bg-red-50 text-red-600 text-xs border-l-2 border-red-500">
                      {error}
                    </motion.div>
                  )}
                  {successMessage && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-3 bg-green-50 text-green-600 text-xs border-l-2 border-green-500">
                      {successMessage}
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Submit */}
                <div className="pt-4 border-t border-gray-50 flex justify-end">
                  <button
                    type="submit"
                    disabled={submitting || !form.bookingTime}
                    className="bg-black text-white px-8 py-3 text-xs tracking-[0.2em] uppercase hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? 'Booking...' : 'Confirm Booking'}
                  </button>
                </div>

              </form>
            </motion.div>
          </div>
        </div>
      </main >
    </div >
  );
}


// ISO-ish country list (common names)

